import type { SidedLayout } from "@pdfking/filling-flex"
import { Min, POS_INFINITY } from "@pdfking/math"
import type { LayerID } from "@pdfking/renderer-api"
import type { PageShape } from "@pdfking/renderer-api"
import { PlotFunction } from "@pdfking/renderer-api"
import { NullQ } from "@pdfking/structs"
import { Map, RecordCreate, Reduce, SortBy } from "@pdfking/tfunc"

import type { ImpositionNode, ImpositionNodeData, PlotCtx } from "#internal"

import { PlaceDrawing } from "./PlaceDrawing"

// A shape on `layer`: a group carries none, its children do.
const on_layer = (shape: PageShape, layer: LayerID): PageShape =>
  shape.enum === PlotFunction.PLOT_GROUP
    ? { ...shape, shapes: shape.shapes.map(child => on_layer(child, layer)) }
    : { ...shape, layer }

/**
 * One sheet's shapes on `layers`: every drawing of every node, layer by layer, evaluated
 * where the node landed. Children draw in page-number order and before the
 * node itself, so what a fill draws over its pages — the crossmarks, the
 * security lines — comes after them, and what a hook hung on the sheet comes
 * last.
 *
 * Under a fill the pages are told so: a grow's measures are left out, and
 * only the outermost fill draws crossmarks — unless a `context_processor`
 * says otherwise.
 */
export const DrawLayout = (
  layout: SidedLayout<ImpositionNodeData>,
  ctx: PlotCtx,
  layers: LayerID[]
): PageShape[] => {
  const shapes = RecordCreate(layers.map(layer => [layer, [] as PageShape[]] as const))

  // Pages draw in the order they print: a node by the first page number under
  // it, the tree's own order among equals. Read once per node, bottom up.
  const firsts = new WeakMap<ImpositionNode, number>()
  const first_page = (node: ImpositionNode): number => {
    const known = firsts.get(node)
    if (!NullQ(known)) return known
    const first = node.page
      ? (ctx.page_number_of(node.page.guid) ?? POS_INFINITY)
      : Reduce((a, b) => Min(a, b), Map(first_page, node.children), POS_INFINITY)
    firsts.set(node, first)
    return first
  }

  const walk = (node: ImpositionNode, current: PlotCtx): void => {
    const fill = node.data?.fill
    const below: PlotCtx = fill
      ? current.context_processor({
          ...current,
          plot_measures: false,
          plot_crossmarks: false,
        })
      : current
    for (const child of SortBy(node.children, first_page)) walk(child, below)
    for (const drawing of node.data?.drawings ?? []) {
      shapes[drawing.layer]?.push(
        ...PlaceDrawing(node, drawing, current).map(shape => on_layer(shape, drawing.layer))
      )
    }
  }
  walk(layout.root, ctx)

  return layers.flatMap(layer => shapes[layer]!)
}
